import React, { useState } from 'react';

import type { UUID } from '@aline/common';
import { OpCode, Select } from '@aline/common';
import { Typography } from '@mui/material';
import clsx from 'clsx';
import type { PanelVariableType } from 'src/components';
import {
  AlineTooltip,
  AnswerOptionsAutocomplete,
  useOnChangeFocusVariable,
  useUpdateOptionsVariableValue,
  useVariableStyles,
  VariableRowHeader,
} from 'src/components';
import { useGetFieldDisabledReason } from 'src/editor/popovers';
import { domElIdCreators } from 'src/helpers';

interface OptionsVariableProps extends React.HTMLAttributes<HTMLElement> {
  documentId: UUID;
  isSelected: boolean;
  optsVariable: PanelVariableType;
}

export function OptionsVariable({
  className,
  documentId,
  isSelected,
  optsVariable,
}: OptionsVariableProps): JSX.Element {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [liEl, setLiEl] = useState<HTMLLIElement | null>(null);
  const { disabledReason } = useGetFieldDisabledReason(optsVariable);
  const [inputEl, setInputEl] = useState<HTMLInputElement | null>(null);
  const {
    onClickVariable,
    options,
    resetOnBlur,
    selectedAnswer,
    setSelectedAnswer,
    updateOptionsVariable,
  } = useUpdateOptionsVariableValue(optsVariable, { isFocused });
  const isCompleted =
    typeof optsVariable.value === 'string' || Boolean(optsVariable.selectedAnswer);
  const { classes } = useVariableStyles({ isCompleted, isSelected });

  const { onFocus } = useOnChangeFocusVariable(optsVariable, { inputEl, liEl });

  return (
    <li
      ref={setLiEl}
      aria-selected={isSelected}
      className={clsx(className, classes.variable)}
      data-cy={Select.document.editor.rightDrawer.variable.list.row.container}
      id={domElIdCreators.variableRowEl(optsVariable.nodeId)}
      onClick={onClickVariable}
      onFocus={onFocus}
    >
      <VariableRowHeader
        documentId={documentId}
        isCompleted={isCompleted}
        variable={optsVariable}
      />
      {optsVariable.question && (
        <Typography className={classes.question}>{optsVariable.question}</Typography>
      )}
      <AlineTooltip arrow placement="top" title={disabledReason}>
        <div>
          <AnswerOptionsAutocomplete
            disabled={Boolean(disabledReason)}
            inputClassName={classes.input}
            inputDataCy={Select.document.editor.rightDrawer.outline.dropDownFieldRowInput}
            inputRef={setInputEl}
            openOnFocus={false}
            options={options}
            placeholder="Select option..."
            slotProps={{ listbox: { id: domElIdCreators.variableListBoxEl(optsVariable.id) } }}
            value={selectedAnswer}
            onBlur={() => {
              const opCode = resetOnBlur();
              if (opCode === OpCode.Success) setSelectedAnswer(null);
              setIsFocused(false);
            }}
            onChange={async (_event, value) => {
              await updateOptionsVariable(value);
            }}
            onFocus={() => {
              setIsFocused(true);
            }}
          />
        </div>
      </AlineTooltip>
    </li>
  );
}
